import './index.css';
// import './providers/wdyr/index.ts';
import './providers/i18n';

import LogoSvg from '@icons/logo.svg?react';
import { applyThemeColors } from '@shared/hooks/useDynamicThemeColor.ts';
import LoadingScreen from '@shared/ui/LoadingScreen/LoadingScreen.tsx';
import { clearChunkUpdateReloadState, recoverFromChunkLoadError } from '@shared/utils/appUpdate.ts';
import React, { Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import { RouterProvider } from 'react-router/dom';

import { AppWrapper } from './providers/AppWrapper.tsx';
import index from './providers/routes';
import { store } from './store/store.ts';

const appUpdateUrl = new URL(window.location.href);
if (appUpdateUrl.searchParams.has('__app_update')) {
  appUpdateUrl.searchParams.delete('__app_update');
  window.history.replaceState(window.history.state, '', appUpdateUrl.toString());
}

window.setTimeout(() => {
  clearChunkUpdateReloadState();
}, 30000);

// biome-ignore lint/style/noNonNullAssertion: <explanation>
ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <Provider store={store}>
      <AppWrapper>
        <Suspense
          fallback={
            <LoadingScreen
              visible={true}
              spinner={<LogoSvg className="fill-current h-8 w-8" />}
              fullScreen={true}
              loadingText="Loading..."
            />
          }
        >
          <RouterProvider router={index} />
        </Suspense>
      </AppWrapper>
    </Provider>
  </React.StrictMode>,
);

// Only register service worker in production builds
if ('serviceWorker' in navigator) {
  window.addEventListener('load', async () => {
    try {
      // Apply initial theme colors for PWA
      const savedTheme = localStorage.getItem('theme') ?? 'light';
      applyThemeColors(savedTheme);

      const registration = await navigator.serviceWorker.register('/service-worker.js', {
        // Always fetch the SW script from network on update check — never serve from
        // the HTTP cache. Prevents stale SW from being re-registered and avoids the
        // "object is in an invalid state" update error caused by cached script mismatches.
        updateViaCache: 'none',
      });
      console.log('Service Worker registered successfully:', registration);

      // Listen for service worker updates
      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        if (newWorker) {
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              // New service worker is available
              console.log('New service worker available');
            }
          });
        }
      });

      // Handle service worker messages
      navigator.serviceWorker.addEventListener('message', event => {
        console.log('Message from service worker:', event.data);
        if (event.data?.type === 'BUILD_ASSET_MISSING') {
          recoverFromChunkLoadError();
        }
      });
    } catch (registrationError) {
      // "The object is in an invalid state" can occur when the browser has a corrupted
      // SW registration (e.g. from a previously broken install). Unregister all stale
      // registrations so the browser starts fresh on next load.
      console.error('SW registration failed:', registrationError);
      if (
        registrationError instanceof DOMException &&
        registrationError.name === 'InvalidStateError'
      ) {
        try {
          const registrations = await navigator.serviceWorker.getRegistrations();
          await Promise.all(registrations.map(r => r.unregister()));
          console.log('Cleared stale SW registrations — will re-register on next load.');
        } catch (_) {
          // best-effort
        }
      }
    }
  });
}
